const SAFE_DATA_URL_PATTERN = /^data:(image|video|audio)\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+$/i;
const SAFE_PROTOCOLS = new Set(["http:", "https:"]);
const MEDIA_SOURCE_TAGS = new Set(["AUDIO", "IMG", "SOURCE", "TRACK", "VIDEO"]);
const UNSAFE_URL_TEXT_PATTERN = /[<>"'`\\]/;
const CONTROL_OR_SPACE_PATTERN = new RegExp(
  `[${String.fromCharCode(0)}-${String.fromCharCode(31)}${String.fromCharCode(127)}\\s]+`,
  "g"
);
let fallbackIdSequence = 0;

export function stripUnsafeCharacters(value) {
  return String(value ?? "")
    .trim()
    .replace(CONTROL_OR_SPACE_PATTERN, "");
}

function sanitizeRelativeUrl(url) {
  const cleaned = String(url ?? "").trim();
  if (!cleaned) return "";
  if (cleaned.startsWith("//")) return "";
  if (UNSAFE_URL_TEXT_PATTERN.test(cleaned)) return "";

  try {
    const baseOrigin = globalThis.location?.origin || "http://localhost";
    const parsed = new URL(cleaned, baseOrigin);
    return parsed.origin === baseOrigin ? parsed.href : "";
  } catch {
    return "";
  }
}

export function sanitizeMediaUrl(url) {
  const rawUrl = String(url ?? "").trim();
  if (!rawUrl) return "";

  if (rawUrl.startsWith("/") || rawUrl.startsWith("./") || rawUrl.startsWith("../")) {
    return sanitizeRelativeUrl(rawUrl);
  }

  if (rawUrl.startsWith("blob:")) {
    return rawUrl;
  }

  if (rawUrl.startsWith("data:")) {
    return SAFE_DATA_URL_PATTERN.test(rawUrl) ? rawUrl : "";
  }

  try {
    const parsed = new URL(rawUrl, window.location.origin);
    if (!SAFE_PROTOCOLS.has(parsed.protocol)) return "";
    return parsed.href;
  } catch {
    return rawUrl.includes(":") ? "" : sanitizeRelativeUrl(rawUrl);
  }
}

export function getSafeMediaSource(url) {
  return sanitizeMediaUrl(url) || undefined;
}

function encodeSafeAttributeUrl(url) {
  try {
    return encodeURI(String(url || "")).replace(/%25([0-9a-f]{2})/gi, "%$1");
  } catch {
    return "";
  }
}

export function applySafeMediaSource(element, url) {
  if (!element || !MEDIA_SOURCE_TAGS.has(element.tagName)) return false;

  const safeUrl = sanitizeMediaUrl(url);
  const safeAttributeUrl = safeUrl ? encodeSafeAttributeUrl(safeUrl) : "";
  const isPlayableMedia = element.tagName === "AUDIO" || element.tagName === "VIDEO";
  if (isPlayableMedia && !element.crossOrigin) {
    element.crossOrigin = "anonymous";
  }
  const reloadPlayableMedia = () => {
    if (isPlayableMedia && typeof element.load === "function") {
      element.load();
    }
  };
  if (!safeAttributeUrl) {
    if (element.hasAttribute("src")) {
      element.removeAttribute("src");
      reloadPlayableMedia();
    }
    return false;
  }

  if (element.getAttribute("src") !== safeAttributeUrl) {
    element.setAttribute("src", safeAttributeUrl);
    reloadPlayableMedia();
  }

  return true;
}

export function applySafeMediaPoster(element, url) {
  if (!element || element.tagName !== "VIDEO") return false;
  const safeUrl = sanitizeMediaUrl(url);
  const encoded = safeUrl ? encodeSafeAttributeUrl(safeUrl) : "";
  if (!encoded) {
    element.removeAttribute("poster");
    return false;
  }
  element.setAttribute("poster", encoded);
  return true;
}

// Helper to sanitize URLs for use in src/href.
export function sanitizeUrl(url) {
  return sanitizeMediaUrl(url);
}

export function createSecureId(prefix = "id") {
  const safePrefix =
    String(prefix || "id")
      .trim()
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/^-+|-+$/g, "") || "id";

  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `${safePrefix}-${globalThis.crypto.randomUUID()}`;
  }

  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const buffer = new Uint32Array(4);
    globalThis.crypto.getRandomValues(buffer);
    const suffix = Array.from(buffer, value => value.toString(16).padStart(8, "0")).join("");
    return `${safePrefix}-${suffix}`;
  }

  fallbackIdSequence = (fallbackIdSequence + 1) % Number.MAX_SAFE_INTEGER;
  return `${safePrefix}-${Date.now().toString(36)}-${fallbackIdSequence.toString(36)}`;
}

// Validate redirect URLs to prevent open redirect attacks.
// Only allows HTTPS URLs on trusted domains, or same-origin relative paths.
const TRUSTED_REDIRECT_HOSTS = [
  "autopromote.org",
  "www.autopromote.org",
  "tibule12.github.io",
  "paypal.com",
  "www.paypal.com",
  "www.sandbox.paypal.com",
];

function normalizeHostname(hostname) {
  return String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.+$/g, "");
}

function isTrustedHostname(hostname, trustedHosts) {
  const normalizedHost = normalizeHostname(hostname);
  if (!normalizedHost) return false;
  return trustedHosts.some(trustedHost => {
    const normalizedTrusted = normalizeHostname(trustedHost);
    return normalizedHost === normalizedTrusted || normalizedHost.endsWith(`.${normalizedTrusted}`);
  });
}

export function isSafeRedirectUrl(url) {
  if (!url) return false;
  // Allow relative paths (same-origin navigation)
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    return isTrustedHostname(parsed.hostname, TRUSTED_REDIRECT_HOSTS);
  } catch {
    return false;
  }
}
